import moment from 'moment';
import React, {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import formsSelectors from '../../store/forms/selectors';
import formsActionCreators from '../../store/forms/action-creators';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import ContentFormCard from './ContentFormCard';
import FormElementEdit from './FormElementEdit';
import HeaderCard from './HeaderCard';
import PromptFormCard from './PromptFormCard';
import QuestionCard from './QuestionCard';
import Modal from '../../../shared/ui/Modal';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';
import ReactTooltip from 'react-tooltip';
import RatingMultiFormCard from "./RatingMultiFormCard";
import uuidv4 from 'uuid/v4';

import './FormDetail.scss';
import AcceptableUnacceptableFormCard from "./AcceptableUnacceptableFormCard";
import CustomForm from "../../../shared/components/form/CustomForm";
import {editFormNameConfig} from "../../../shared/components/form/CreateFormConfig";
import RemedialTrainingFieldFormCard from "./RemedialTrainingFieldFormCard";
import CallTrackingFieldCard from "./CallTrackingFieldCard";
import DailyPatrolLogFieldCard from "./DailyPatrolLogFieldCard";
import LogFieldCard from "./LogFieldCard";
import QuestionFieldCard from "./QuestionFieldCard";
import CheckoffFormCard from "./CheckoffFormCard";
import PtoRatingMultiFormCard from "./PtoRatingMultiFormCard";
import TimeTrackingForm from "./TimeTrackingForm";
import PbleForm from "./PbleForm";
import EvaluationRatingFormCard, {DEFAULT_SCORE_OVERRIDES} from "./EvaluationRatingFormCard";

const DragHandle = SortableHandle(() =>
  <div className='move-item' data-tip="Drag phases to reorder">
    <ReactTooltip place="top" className='customTheme' effect="solid"/>
    <i className='fa fa-chevron-up'/>
    <i className='fa fa-chevron-down'/>
  </div>
);

const SortableItem = SortableElement(({element, canEdit, getFormElementCardActionsFor, elementIndex, deleteElement, editElement, formRating}) =>
  <div className="single-form-detail">
    {canEdit && <DragHandle/>}
    {element.type === 'header' ? (
      <HeaderCard
        actions={getFormElementCardActionsFor(elementIndex)}
        element={element}
        canEdit={canEdit}
        onDelete={() => deleteElement(elementIndex)}
        onEdit={() => editElement(elementIndex, 'header')}
      />
      ) : (
        <QuestionCard
          actions={getFormElementCardActionsFor(elementIndex)}
          element={element}
          canEdit={canEdit}
          onDelete={() => deleteElement(elementIndex)}
          onEdit={() => editElement(elementIndex, element.schema)}
          schemaType={element.schema}
          formRating={formRating}
        />
      )}
    </div>
);

const SortableFormDetails = SortableContainer(({elements, canEdit, getFormElementCardActionsFor, deleteElement, editElement, formRating}) => {
  return (
    <form className="form-detail-preview">
      {elements.map((element, index) => (
        <SortableItem
          key={`item-${index}`}
          index={index}
          elementIndex={index}
          element={element}
          canEdit={canEdit}
          getFormElementCardActionsFor={getFormElementCardActionsFor}
          deleteElement={deleteElement}
          editElement={editElement}
          formRating={formRating}
        />
      ))}
    </form>
  );
});

const getDefaultValuesForType = (type) => {
  if (type === 'remedial-training') {
    return {
      title_label: 'Remedial training log',
      name_label: 'Name of the remedial',
      hours_spend_label: 'Hours spend',
      reason_label: 'Reason',
      remedial_training_successful_label: 'Was the remedial training successful?',
      recap_of_results_label: 'Recap of the results',
      continue_remedial_training_label: 'Continue remedial training?'
    }
  } else if (type === 'call-tracking') {
    return {
      title_label: 'Call tracking',
      call_type_label: 'Call type (accident, fight, etc..)',
      time_label: 'Time',
      location_label: 'Location',
      was_there_arrest_label: 'Was there an arrest?',
      overview_label: 'Overview/Comments/Recap of learning experience',
      rating_label: 'Rating',
    }
  }
  else if (type === 'daily-patrol-log') {
    return {
      title_label: 'Daily patrol log',
      type_label: 'Type',
      location_label: 'Location',
      activity_label: 'Activity',
      action_label: 'Action',
    }
  }
  else if (type === 'log-field') {
    return {
      title_label: 'Log Field',
    }
  }
  else if (type === 'question-field') {
    return {
      title_label: 'Question Field',
      dropdown: true,
      fields: {
        subtitle: '',
        answers: []
      }
    }
  }
  else if (type === 'time-field') {
    return {
      title_label: 'Time tracking',
    }
  }
  else if (type === 'evaluation-rating') {
    return {
      ratingPrompt: '',
      scale: 3,
      notObservedPrompt: 'N/O',
      notResponsivePrompt: 'N/R',
      defaultComments: [],
      requiredComments: [],
      overrideDefaultScores: true,
      scoreOverrides: DEFAULT_SCORE_OVERRIDES,
      useNotObserved: true,
      useNotResponsive: true
    }
  }
  else {
    return {
      prompt: ''
    }
  }
};

class FormDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false,
      isEditingElement: false,
      tempLayout: null,
      isEditFormNameOpened: false
    };
  }

  // ----------------------
  // Lifecycle
  // ----------------------

  componentWillMount() {
    if (this.props.match.params.formGuid !== this.props.selectedGuid) {
      this.props.setSelectedForm(this.props.match.params.formGuid);
    }
  }

  // ----------------------
  // Computed
  // ----------------------

  get isLoading() {
    return this.props.isLoading.includes('fetchForms') || !this.props.selectedForm;
  }

  get isUpdateFormLoading() {
    return this.props.isLoading.includes('updateForm')
  }

  get canEdit() {
    return this.props.userContext.hasScope(['depadmin', 'admin']) && this.props.selectedForm.status === 'draft'
  }

  getFormElementCardActionsFor(index) {
    return {
      canDelete: this.props.selectedForm.layout.length > 1,
      canEdit: true,
      // canMoveDown: this.props.selectedForm.layout.length > index + 1,
      // canMoveUp: this.hasOrientation ? index > 1 : index > 0,
    };
  }

  // ----------------------
  // Methods
  // ----------------------

  closeEditElement() {
    this.setState({isEditingElement: false});
  }

  deleteElement(index) {
    const layout = [...this.props.selectedForm.layout];
    layout.splice(index, 1);
    this.props.updateForm(this.props.selectedGuid, {layout});
  }

  editElement(index, typeKey) {
    this.setState({
      isEditingElement: {
        index,
        typeKey,
      },
    });
  }

  reIndexLayout(oldIndex, newIndex) {
    const layout = [...this.props.selectedForm.layout];
    const moving = layout.splice(oldIndex, 1);
    layout.splice(newIndex, 0, moving[0]);

    this.setState({
      tempLayout: layout
    });

    this.props.updateForm(this.props.selectedGuid, {layout})
      .then(() => {
        this.setState({
          tempLayout: null
        });
      });
  }

  updateElement(formData) {
    const {index} = this.state.isEditingElement;
    const layout = [...this.props.selectedForm.layout];
    layout[index] = {...formData, id: formData.id || uuidv4()};
    this.props.updateForm(this.props.selectedGuid, {layout});
    this.setState({isEditingElement: false});
  }

  addElement(type, schema) {
    const layout = [{
      id: uuidv4(),
      type,
      schema,
      ...getDefaultValuesForType(schema)
    }, ...this.props.selectedForm.layout];

    this.props.updateForm(this.props.selectedGuid, {layout})
      .then(() => {
        this.editElement(0, schema || type);
      });

  }

  onSortEnd({oldIndex, newIndex})  {
    if (oldIndex !== newIndex) {
      this.reIndexLayout(oldIndex, newIndex);
    }
  };

  // ----------------------
  // State setters
  // ----------------------

  toggle() {
    this.setState({dropdownOpen: !this.state.dropdownOpen});
  }

  openForm() {
    this.setState({
      isEditFormNameOpened: true
    })
  }

  updateFormName(form) {
    this.props.updateForm(this.props.selectedGuid, form)
      .then(() => {
        this.setState({
          isEditFormNameOpened: false
        })
      })
  }

  get selectedFormHeader() {
    if (this.props.selectedForm) {
      return {
        name: this.props.selectedForm.name,
        scale: this.props.selectedForm.settings ? this.props.selectedForm.settings.scale : undefined,
        riskAlert: this.props.selectedForm.settings ? this.props.selectedForm.settings.riskAlert : undefined,
        requireAckComment: this.props.selectedForm.settings ? this.props.selectedForm.settings.requireAckComment : undefined,

      }
    } else {
      return {}
    }
  }

  get formRating() {
    if (!this.props.selectedForm) {
      return undefined
    }
    return this.props.selectedForm.settings ? this.props.selectedForm.settings.scale : undefined
  }

  closeEditForm() {
    this.setState({
      isEditFormNameOpened: false
    })
  }

  // ----------------------
  // Render
  // ----------------------

  render() {
    return (
      <LoadingOverlay isLoading={this.isLoading}>
        <Fragment>
          {!this.isLoading && (
            <Fragment>
              <div className="app-content-header">
                <h4 className="title">{this.props.selectedForm.name} &nbsp;
                  <i className="badge badge-primary"> {this.props.selectedForm.status} </i>
                  {this.canEdit && (
                    <button className="button secondary small ml-2" onClick={() => this.openForm()}>
                      <i className="fa fa-edit"/>
                      <span>Edit</span>
                    </button>
                  )}
                </h4>
                <small>
                  <i>
                    Updated At: {moment(this.props.selectedForm.updatedAt).format('MMM D, YYYY')}
                  </i>
                </small>
              </div>
              <div className="app-content form-detail">
                <div>
                  {this.canEdit && (
                    <div className="manage-form-actions">
                      <span className="add">ADD</span>
                      <button className="button primary small" onClick={() => this.addElement('header')}><i className="fa fa-header"/> Header</button>
                      <button className="button primary small" onClick={() => this.addElement('question', 'checkoff')}><i className="fa fa-check"/> Checkoff</button>
                      <button className="button primary small" onClick={() => this.addElement('question', 'comment')}><i className="fa fa-comment"/> Comment</button>
                      <button className="button primary small" onClick={() => this.addElement('question', 'datetime')}><i className="fa fa-calendar"/> Datetime</button>
                      <button className="button primary small" onClick={() => this.addElement('question', 'rating')}><i className="fa fa-star"/> Rating</button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'rating-multi')}><i
                        className="fa fa-star"/> Rating multi
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'pto-rating-multi')}><i
                        className="fa fa-star"/>PTO Rating multi
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'evaluation-rating')}><i
                        className="fa fa-star"/>Evaluation rating
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'rating-simple')}><i className="fa fa-star"/>Yes/no
                        rating
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'remedial-training')}><i
                        className="fa fa-calendar-plus-o"/>Remedial training list
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'call-tracking')}><i className="fa fa-phone"/>Call
                        tracking list
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'daily-patrol-log')}><i
                        className="fa fa-phone"/>Daily Patrol Log
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'log-field')}><i
                        className="fa fa-phone"/>Log Field
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'question-field')}><i
                        className=""/>Dropdown/checklist Field
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'time-field')}><i
                        className=""/>Time tracking field
                      </button>
                      <button className="button primary small"
                              onClick={() => this.addElement('question', 'pble')}><i
                        className=""/>PBLE Field
                      </button>
                    </div>

                  )}
                </div>
                <SortableFormDetails
                  lockAxis={'y'}
                  lockToContainerEdges
                  useDragHandle
                  helperClass='dragged-form-element'
                  onSortEnd={this.onSortEnd.bind(this)}
                  elements={this.state.tempLayout || this.props.selectedForm.layout}
                  canEdit={this.canEdit}
                  getFormElementCardActionsFor={this.getFormElementCardActionsFor.bind(this)}
                  deleteElement={this.deleteElement.bind(this)}
                  editElement={this.editElement.bind(this)}
                  formRating={this.formRating}
                />
              </div>
            </Fragment>
          )}

        </Fragment>

        <Modal
          isOpen={!!this.state.isEditingElement}
          onClose={() => this.closeEditElement()}
          title={"Edit"}>
          {
            !!this.state.isEditingElement && (
              <Fragment>
                {this.state.isEditingElement.typeKey === 'header' ? (
                  <FormElementEdit
                    formComponent={ContentFormCard}
                    isLoading={this.isUpdateFormLoading}
                    element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                    onUpdate={formData => this.updateElement(formData)}
                  />
                ) :  this.state.isEditingElement.typeKey === 'rating-multi' ? (
                  <FormElementEdit
                    formComponent={RatingMultiFormCard}
                    isLoading={this.isUpdateFormLoading}
                    element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                    onUpdate={formData => this.updateElement(formData)}
                  />
                  ): this.state.isEditingElement.typeKey === 'rating-simple' ? (
                    <FormElementEdit
                      formComponent={AcceptableUnacceptableFormCard}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)} />
                ) : this.state.isEditingElement.typeKey === 'remedial-training' ?
                  (
                    <FormElementEdit
                      formComponent={RemedialTrainingFieldFormCard}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}/>
                  ) : this.state.isEditingElement.typeKey === 'call-tracking' ? (
                    <FormElementEdit
                      formComponent={CallTrackingFieldCard}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}/>
                  ) : this.state.isEditingElement.typeKey === 'daily-patrol-log' ? (
                      <FormElementEdit
                        formComponent={DailyPatrolLogFieldCard}
                        isLoading={this.isUpdateFormLoading}
                        element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                        onUpdate={formData => this.updateElement(formData)}
                      />
                  ) : this.state.isEditingElement.typeKey === 'log-field' ? (
                      <FormElementEdit
                        formComponent={LogFieldCard}
                        isLoading={this.isUpdateFormLoading}
                        element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                        onUpdate={formData => this.updateElement(formData)}
                      />
                  ) :this.state.isEditingElement.typeKey === 'question-field' ? (
                    <FormElementEdit
                      formComponent={QuestionFieldCard}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}
                    />
                  ) : this.state.isEditingElement.typeKey === 'checkoff' ? (
                    <FormElementEdit
                      formComponent={CheckoffFormCard}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}
                    />
                  ) : this.state.isEditingElement.typeKey === 'time-field' ? (
                    <FormElementEdit
                      formComponent={TimeTrackingForm}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}
                    />
                  ) : this.state.isEditingElement.typeKey === 'pble' ? (
                    <FormElementEdit
                      formComponent={PbleForm}
                      isLoading={this.isUpdateFormLoading}
                      element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                      onUpdate={formData => this.updateElement(formData)}
                    />
                  ) : this.state.isEditingElement.typeKey === 'pto-rating-multi' ?
                    (
                      <FormElementEdit
                        formComponent={PtoRatingMultiFormCard}
                        isLoading={this.isUpdateFormLoading}
                        element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                        onUpdate={formData => this.updateElement(formData)}
                      />
                    ) :
                    this.state.isEditingElement.typeKey === 'evaluation-rating' ?
                    (
                      <FormElementEdit
                        formComponent={EvaluationRatingFormCard}
                        isLoading={this.isUpdateFormLoading}
                        element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                        onUpdate={formData => this.updateElement(formData)}
                      />
                    ) :
                    (
                      <FormElementEdit
                        formComponent={PromptFormCard}
                        isLoading={this.isUpdateFormLoading}
                        element={this.props.selectedForm.layout[this.state.isEditingElement.index]}
                        onUpdate={formData => this.updateElement(formData)}
                      />
                    )
                }
              </Fragment>
            )
          }

        </Modal>
        <Modal
          isOpen={this.state.isEditFormNameOpened}
          title="Edit form name"
          onClose={() => this.closeEditForm()}
        >
          {this.state.isEditFormNameOpened && (
            <CustomForm
              initialValues={this.selectedFormHeader}
              config={editFormNameConfig}
              onSubmit={(form) => this.updateFormName(form)}
            />
          )}
        </Modal>
      </LoadingOverlay>


    );
  }
}

// ----------------------
// Redux Mappings
// ----------------------

const mapStateToProps = (state) => {
  return {
    isLoading: state.forms.isLoading,
    selectedGuid: state.forms.selectedGuid,
    selectedForm: formsSelectors.selectedForm(state),
    userContext: state.common.userContext,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    editForm: () => {
      return dispatch(formsActionCreators.editForm());
    },
    setSelectedForm: (formGuid) => {
      return dispatch(formsActionCreators.setSelectedForm(formGuid));
    },
    updateForm: (formGuid, formData) => {
      return dispatch(formsActionCreators.updateForm(formGuid, formData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormDetail);
